import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  securityEvents,
  type SecurityEvent,
  type SecurityEventParams,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Pagination } from '@/shared/ui/Pagination';
import { Select } from '@/shared/ui/Select';

import { detalheDoEvento, eventoLegivel, opcoesDoEvento } from './rotulos';

/**
 * Aba Acessos da tela de logs: entradas, saidas, renovacoes de sessao, trocas de
 * senha e de empresa (`security_events`), que antes so eram gravadas.
 *
 * A falha de login e gravada sem empresa, porque a pessoa ainda nao escolheu a
 * empresa quando erra a senha, e por isso nao aparece aqui. Quem nao le os usuarios
 * da empresa ve o e-mail e o nome mascarados e nao filtra por pessoa.
 */

const LIMITE = 25;

interface Filtros {
  from: string;
  to: string;
  action: string;
  email: string;
}

const filtrosVazios: Filtros = { from: '', to: '', action: '', email: '' };

export function LogsAccessTab() {
  const { can } = useAuth();
  const lePessoas = can('usuarios.usuario.view');

  const [rascunho, setRascunho] = useState<Filtros>(filtrosVazios);
  const [aplicados, setAplicados] = useState<Filtros>(filtrosVazios);
  const [offset, setOffset] = useState(0);

  const parametros: SecurityEventParams = {
    limit: LIMITE,
    offset,
    from: aplicados.from,
    to: aplicados.to,
    action: aplicados.action,
    email: lePessoas ? aplicados.email.trim() : '',
  };

  const consulta = useQuery({
    queryKey: ['security-events', parametros],
    queryFn: ({ signal }) => securityEvents.list(parametros, signal),
    placeholderData: keepPreviousData,
  });

  const muda = (campo: keyof Filtros) => (valor: string) =>
    setRascunho((atual) => ({ ...atual, [campo]: valor }));

  const colunas: Column<SecurityEvent>[] = [
    {
      key: 'quando',
      header: 'Data e hora',
      render: (evento) => (
        <span className="tabular-nums">{formatDateTime(evento.occurred_at)}</span>
      ),
    },
    {
      key: 'evento',
      header: 'Evento',
      render: (evento) => (
        <div className="min-w-0">
          <p className="truncate text-ink-900">{eventoLegivel(evento.action)}</p>
          <p className="truncate text-2xs text-ink-500">{evento.action}</p>
        </div>
      ),
    },
    {
      key: 'quem',
      header: 'Quem',
      render: (evento) => (
        <div className="min-w-0">
          <p className="truncate text-ink-900">
            {evento.user_name || evento.email || 'sem identificacao'}
          </p>
          {evento.user_name && evento.email ? (
            <p className="truncate text-2xs text-ink-500">{evento.email}</p>
          ) : null}
          {evento.person_masked ? <Badge tone="neutral">mascarado</Badge> : null}
        </div>
      ),
    },
    {
      key: 'origem',
      header: 'Origem',
      hideBelow: 'md',
      render: (evento) =>
        evento.ip || <span className="text-ink-400">nao registrada</span>,
    },
    {
      key: 'navegador',
      header: 'Navegador',
      hideBelow: 'lg',
      render: (evento) => (
        <span className="line-clamp-2 break-all text-2xs text-ink-600">
          {evento.user_agent || 'nao registrado'}
        </span>
      ),
    },
    {
      key: 'detalhe',
      header: 'Detalhe',
      render: (evento) => detalheDoEvento(evento.details) || '-',
    },
  ];

  const pagina = consulta.data;
  const filtrando = Object.values(aplicados).some(Boolean);

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardBody className="flex flex-col gap-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
            Filtros
          </h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field
              label="Data inicial"
              type="date"
              value={rascunho.from}
              onChange={(evento) => muda('from')(evento.target.value)}
            />
            <Field
              label="Data final"
              type="date"
              value={rascunho.to}
              onChange={(evento) => muda('to')(evento.target.value)}
            />
            <Select
              label="Evento"
              placeholder="Todos"
              options={opcoesDoEvento}
              value={rascunho.action}
              onChange={(evento) => muda('action')(evento.target.value)}
            />
            <Field
              label="E-mail de quem entrou"
              value={rascunho.email}
              disabled={!lePessoas}
              hint={
                lePessoas
                  ? undefined
                  : 'O filtro por pessoa exige a leitura dos usuarios da empresa.'
              }
              onChange={(evento) => muda('email')(evento.target.value)}
            />
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setRascunho(filtrosVazios);
                setAplicados(filtrosVazios);
                setOffset(0);
              }}
            >
              Limpar filtro
            </Button>
            <Button
              onClick={() => {
                setAplicados(rascunho);
                setOffset(0);
              }}
            >
              Aplicar
            </Button>
          </div>
        </CardBody>
      </Card>

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar os acessos">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Card>
        <div className="flex items-center justify-end border-b border-line px-4 py-2">
          <span className="text-xs text-ink-600">{pagina?.total ?? 0} registros</span>
        </div>
        <DataTable
          columns={colunas}
          items={pagina?.items ?? []}
          keyOf={(evento) => evento.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon="lock"
              title={
                filtrando ? 'Nenhum acesso encontrado' : 'Nenhum acesso registrado'
              }
              description={
                filtrando
                  ? 'Ajuste os filtros para ver outros acessos.'
                  : 'Cada entrada, saida e troca de senha da empresa aparece aqui.'
              }
            />
          }
        />
        {pagina ? (
          <Pagination
            total={pagina.total}
            limit={pagina.limit}
            offset={pagina.offset}
            onChange={setOffset}
          />
        ) : null}
      </Card>

      <p className="text-2xs text-ink-500">
        A falha de entrada e o pedido de troca de senha pelo link sao gravados sem a
        empresa, porque a pessoa ainda nao escolheu a empresa nesse momento, e por isso
        nao aparecem nesta lista.
      </p>
    </div>
  );
}
